import React, { useContext, useEffect, useState } from 'react'
import EPCR, { EPCRContext } from '../EPCR'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  Grid,
  List,
  ListItem,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../../../services/API'
import validator from 'validator'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import { useNavigate } from 'react-router-dom'
import {
  CloudDone,
  CloudDownload,
  CloudOff,
  CloudSync,
  CloudUpload,
} from '@mui/icons-material'
import { syncStatusColors } from '../EPCRHeader'
import AnDateTimePicker from '../../../../components/DateTime/DateTimePicker'
import AnDateField from '../../../../components/DateTime/DateField'
import { TenantSettingsContext } from '../../../../App'
import RecordOverview from './RecordOverview'
import { isFullLayoutMode } from '../formTypes'
import { listPendingDetails } from '../liveCapture/engine'
import { REVIEW_TRIGGER_NOTE } from '../liveCapture/catalogue'
import LiveDetailForm from '../liveCapture/LiveDetailForm'
import PendingDetailsList from '../liveCapture/PendingDetailsList'
import {
  welfareCategories,
  welfareOutcomes,
} from '../Welfare/welfareOptions'

const patientOutcomeOptions = (orgShortName) => [
  {
    value: `Transported to Hospital by ${orgShortName}`,
    label: `Transported to Hospital by ${orgShortName}`,
  },
  {
    value: 'Discharged following Treatment',
    label: 'Discharged following Treatment',
  },
  { value: 'Discharged - Advice Only', label: 'Discharged - Advice Only' },
  { value: 'Left before Treatment', label: 'Left before Treatment' },
  {
    value: 'Handed over to Ambulance Crew',
    label: 'Handed over to Ambulance Crew',
  },
  { value: 'Patient Deceased', label: 'Patient Deceased' },
  {
    value: 'Refused Treatment or Transport',
    label: 'Refused Treatment or Transport',
  },
  { value: 'A&E - Own Transport', label: 'A&E - Other Transport' },
  { value: 'Referred - Other', label: 'Referred - Other' },
  { value: 'Other', label: 'Other' },
]

const incidentTypeOptions = [
  'Event',
  '999',
  'Patient Transfer',
  'Off Duty or Travelling',
  'Other',
]

const TempReview = ({ scrollToSection }) => {
  const { epcrPracticeMode, setSiteMode, setEpcrPracticeMode } =
    useContext(SiteModeContext)
  const {
    formValues,
    handleChange,
    setSelectedEPCRModule,
    isDraftDirty,
    isSaving,
    uploadState,
    downloadState,
    onExit,
    saveDraft,
  } = useContext(EPCRContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [requirements, setRequirements] = useState([])
  const navigate = useNavigate()

  const [showDirtyDraftWarning, setShowDirtyDraftWarning] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [detailRequest, setDetailRequest] = useState(null)
  const isFullLayout = isFullLayoutMode(formValues.mode)
  const isCriticalCare = formValues.mode === 'Critical Care'
  // Not a blocker - a reminder of console taps still waiting for details
  const pendingDetails = isFullLayout ? listPendingDetails(formValues) : []

  const isUploading = uploadState === 'pending'
  const isDownloading = downloadState === 'pending'
  const hasSyncError = uploadState === 'error' || downloadState === 'error'
  const isFullySynced = uploadState === 'done' && downloadState === 'done'
  const hasTreatmentLocationOwner =
    !!formValues?.review?.currentTreatmentLocation
  const treatmentLocationName =
    formValues?.review?.currentTreatmentLocation?.name ||
    'the treatment location'
  const orgShortName = orgSettings?.orgShortName || 'AmbuNet'

  const checkRequirements = () => {
    const issues = []

    if (
      (formValues?.incident?.incidentType === 'Event' &&
        ['', null, undefined].includes(formValues?.incident?.linkedEvent)) ||
      !formValues?.incident?.incidentType
    ) {
      issues.push(
        { fieldName: 'Event', tab: 'Incident' },
        { fieldName: 'Incident Type', tab: 'Incident' },
      )
    }

    if (!formValues?.incident?.clearTime) {
      issues.push({ fieldName: 'Incident Clear Time', tab: 'Incident' })
    }

    // Welfare records are non-clinical: only the welfare fields are required
    if (formValues.mode === 'Welfare') {
      if (!formValues?.welfare?.category) {
        issues.push({ fieldName: 'Welfare Category', tab: 'Welfare' })
      }
      if (!formValues?.welfare?.details) {
        issues.push({ fieldName: 'Welfare Details', tab: 'Welfare' })
      }
      if (!formValues?.welfare?.outcome) {
        issues.push({ fieldName: 'Welfare Outcome', tab: 'Welfare' })
      }
      return issues
    }

    if (isFullLayout && !formValues?.incident?.patientSideTime) {
      issues.push({ fieldName: 'Patient Side Time', tab: 'Incident' })
    }

    // Critical care records don't send the patient feedback SMS
    if (
      !isCriticalCare &&
      formValues?.demographics?.consentsToSMS &&
      !formValues?.demographics?.numberForFeedback
    ) {
      issues.push({ fieldName: 'Phone Number', tab: 'Demographics' })
    }

    if (
      !formValues?.demographics?.lastNameUnknown &&
      !formValues?.demographics?.lastName
    ) {
      issues.push({ fieldName: 'Patient Surname', tab: 'Demographics' })
    }

    if (
      !formValues?.demographics?.dobUnknown &&
      !formValues?.demographics?.dob
    ) {
      issues.push({ fieldName: 'Patient DOB', tab: 'Demographics' })
    }

    if (!formValues?.plan?.ptOutcome) {
      issues.push({ fieldName: 'Patient Outcome', tab: 'Plan' })
    }

    if (
      isFullLayout &&
      formValues?.safeguarding?.concerns !== true &&
      formValues?.safeguarding?.concerns !== false
    ) {
      issues.push({ fieldName: 'Safeguarding', tab: 'Plan' })
    }

    return issues
  }

  useEffect(() => {
    setRequirements(checkRequirements())
  }, [])

  const updateField = ({ moduleName, field, value }) => {
    handleChange({ moduleName, field, value })
  }

  const hasRequirement = (fieldName) =>
    requirements.some((requirement) => requirement.fieldName === fieldName)

  const handleSubmit = async () => {
    setSubmitError(null)
    const issues = checkRequirements()
    setRequirements(issues)

    if (issues.length > 0) return

    if (isSaving) {
      setShowDirtyDraftWarning(true)
      return
    }

    setSubmitting(true)

    try {
      if (isDraftDirty || uploadState !== 'done') {
        const saved = await saveDraft()

        if (!saved) {
          setSubmitError(
            'This ePCR could not be synced, so it has not been submitted. Please check your connection and try again.',
          )
          setShowDirtyDraftWarning(true)
          return
        }
      }

      const res = await API.put(
        '/epcr/' + formValues._id,
        {
          ...formValues,
          review: {
            status: 'Submitted',
            submittedTime: moment().toDate(),
          },
        },
        {
          skipGlobalErrorHandler: true,
        },
      )

      if (res.status !== 200) {
        setSubmitError(
          'This ePCR could not be submitted. Please check your connection and try again.',
        )
        return
      }

      if (hasTreatmentLocationOwner && onExit) {
        onExit()
      } else {
        window.location.reload()
      }
    } catch (e) {
      console.log(e)
      setSubmitError(
        'This ePCR could not be submitted. Please check your connection and try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return epcrPracticeMode ? (
    <EPCRCard>You cannot submit Practice Records.</EPCRCard>
  ) : (
    <Stack gap={1}>
      {pendingDetails.length > 0 && (
        <EPCRCard>
          <Stack gap={1.5}>
            <Alert
              severity="warning"
              action={
                isCriticalCare && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setSelectedEPCRModule('Live')}
                  >
                    Open live console
                  </Button>
                )
              }
            >
              {pendingDetails.length === 1
                ? '1 logged intervention is still waiting for its details.'
                : `${pendingDetails.length} logged interventions are still waiting for their details.`}{' '}
              You can still submit, but completing them now keeps the record
              accurate.
            </Alert>
            <PendingDetailsList
              items={pendingDetails}
              onOpen={setDetailRequest}
            />
          </Stack>
        </EPCRCard>
      )}
      {isCriticalCare && (
        <Alert severity="info">{REVIEW_TRIGGER_NOTE}</Alert>
      )}
      <LiveDetailForm
        request={detailRequest}
        onClose={() => setDetailRequest(null)}
      />
      <EPCRCard>
        {requirements.length === 0 ? (
          <Stack spacing={1}>
            <Typography>
              Pressing the button below will submit the ePCR. You will no longer
              be able to edit it.
            </Typography>
            {hasTreatmentLocationOwner && (
              <Alert severity="warning">
                Submitting and finalising this ePCR will discharge the patient
                from {treatmentLocationName} and remove them from the treatment
                location board.
              </Alert>
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography variant="title3">
              Complete the missing fields below, or jump to the relevant tab.
            </Typography>
            <Box>
              <Grid container spacing={2}>
                {hasRequirement('Incident Type') && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Incident Type</InputLabel>
                      <Select
                        label="Incident Type"
                        value={formValues?.incident?.incidentType ?? ''}
                        onChange={(e) =>
                          updateField({
                            moduleName: 'incident',
                            field: 'incidentType',
                            value: e.target.value,
                          })
                        }
                      >
                        {incidentTypeOptions.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {hasRequirement('Patient Side Time') && (
                  <Grid item xs={12} md={6}>
                    <AnDateTimePicker
                      label="With Patient"
                      date={formValues?.incident?.patientSideTime}
                      onChange={(value) =>
                        updateField({
                          moduleName: 'incident',
                          field: 'patientSideTime',
                          value,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                )}

                {hasRequirement('Incident Clear Time') && (
                  <Grid item xs={12} md={6}>
                    <AnDateTimePicker
                      label="Incident Clear Time"
                      date={formValues?.incident?.clearTime}
                      onChange={(value) =>
                        updateField({
                          moduleName: 'incident',
                          field: 'clearTime',
                          value,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                )}

                {hasRequirement('Phone Number') && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Phone Number"
                      value={formValues?.demographics?.numberForFeedback ?? ''}
                      onChange={(e) =>
                        updateField({
                          moduleName: 'demographics',
                          field: 'numberForFeedback',
                          value: e.target.value,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                )}

                {hasRequirement('Patient Surname') && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Patient Surname"
                      value={formValues?.demographics?.lastName ?? ''}
                      onChange={(e) =>
                        updateField({
                          moduleName: 'demographics',
                          field: 'lastName',
                          value: e.target.value,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                )}

                {hasRequirement('Patient DOB') && (
                  <Grid item xs={12} md={6}>
                    <AnDateField
                      label="Patient DOB"
                      date={formValues?.demographics?.dob}
                      onChange={(value) =>
                        updateField({
                          moduleName: 'demographics',
                          field: 'dob',
                          value,
                        })
                      }
                      fullWidth
                      disableFuture
                    />
                  </Grid>
                )}

                {hasRequirement('Patient Outcome') && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Patient Outcome</InputLabel>
                      <Select
                        label="Patient Outcome"
                        value={formValues?.plan?.ptOutcome ?? ''}
                        onChange={(e) =>
                          updateField({
                            moduleName: 'plan',
                            field: 'ptOutcome',
                            value: e.target.value,
                          })
                        }
                      >
                        {patientOutcomeOptions(orgShortName).map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {hasRequirement('Safeguarding') && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Safeguarding Concerns</InputLabel>
                      <Select
                        label="Safeguarding Concerns"
                        value={
                          formValues?.safeguarding?.concerns === true
                            ? 'true'
                            : formValues?.safeguarding?.concerns === false
                              ? 'false'
                              : ''
                        }
                        onChange={(e) => {
                          updateField({
                            moduleName: 'safeguarding',
                            field: 'concerns',
                            value: e.target.value === 'true',
                          })
                          // Keep the stored assessment in step with the boolean
                          updateField({
                            moduleName: 'safeguarding',
                            field: 'concernsAssessment',
                            value: e.target.value === 'true' ? 'yes' : 'no',
                          })
                        }}
                      >
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {hasRequirement('Welfare Category') && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Welfare Category</InputLabel>
                      <Select
                        label="Welfare Category"
                        value={formValues?.welfare?.category ?? ''}
                        onChange={(e) =>
                          updateField({
                            moduleName: 'welfare',
                            field: 'category',
                            value: e.target.value,
                          })
                        }
                      >
                        {welfareCategories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {hasRequirement('Welfare Outcome') && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Welfare Outcome</InputLabel>
                      <Select
                        label="Welfare Outcome"
                        value={formValues?.welfare?.outcome ?? ''}
                        onChange={(e) =>
                          updateField({
                            moduleName: 'welfare',
                            field: 'outcome',
                            value: e.target.value,
                          })
                        }
                      >
                        {welfareOutcomes.map((outcome) => (
                          <MenuItem key={outcome} value={outcome}>
                            {outcome}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {hasRequirement('Welfare Details') && (
                  <Grid item xs={12}>
                    <TextField
                      label="What Happened"
                      multiline
                      minRows={2}
                      value={formValues?.welfare?.details ?? ''}
                      onChange={(e) =>
                        updateField({
                          moduleName: 'welfare',
                          field: 'details',
                          value: e.target.value,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {requirements.map((r) => (
                <Button
                  key={r.fieldName}
                  onClick={() =>
                    isFullLayout && setSelectedEPCRModule(r.tab)
                  }
                  size="small"
                  variant="outlined"
                >
                  {r.fieldName}
                </Button>
              ))}
            </Stack>
          </Stack>
        )}
        {requirements.length > 0 && (
          <Button
            onClick={() => setRequirements(checkRequirements())}
            variant="contained"
            color="anPurple"
          >
            Check Again
          </Button>
        )}
      </EPCRCard>
      <RecordOverview hasRequirements={requirements.length > 0} />
        {!isFullLayout && (
        <EPCRCard>
          <Button
            onClick={() => {
              setSiteMode('normal')
              setEpcrPracticeMode(false)
              setSelectedEPCRModule('')
              navigate('/epcr')
            }}
            variant="contained"
          >
            Save as Draft
          </Button>
        </EPCRCard>
      )}
      <EPCRCard>
        {submitError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {submitError}
          </Alert>
        )}
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          disabled={
            !formValues._id ||
            requirements.length > 0 ||
            epcrPracticeMode ||
            submitting
          }
        >
          {submitting ? 'Submitting...' : 'Submit & Finalise'}
        </Button>
      </EPCRCard>
      <Dialog
        open={showDirtyDraftWarning}
        onClose={() => {
          if (isFullySynced) setShowDirtyDraftWarning(false)
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, overflow: 'hidden' },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {isFullySynced ? (
              <CloudDone color="success" />
            ) : hasSyncError ? (
              <CloudOff color="error" />
            ) : (
              <CloudSync color="warning" />
            )}

            <Typography variant="h6" fontWeight={700}>
              {isFullySynced
                ? 'ePCR Synced'
                : hasSyncError
                  ? 'Sync Problem'
                  : 'Syncing ePCR'}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            {!isFullySynced && !hasSyncError && <LinearProgress />}

            <Alert
              severity={
                isFullySynced ? 'success' : hasSyncError ? 'error' : 'warning'
              }
              sx={{ borderRadius: 3 }}
            >
              {isFullySynced
                ? 'This ePCR has been synced successfully. You can safely submit this ePCR.'
                : hasSyncError
                  ? 'This ePCR could not be synced. Please check your connection before leaving.'
                  : 'This ePCR is still syncing. Please wait before leaving this page.'}
            </Alert>
            <Stack direction="row" gap={1} justifyContent="center">
              <CloudUpload
                sx={{
                  color: () => syncStatusColors(uploadState),
                  width: '4rem',
                  height: '4rem',
                }}
              />
              <CloudDownload
                sx={{
                  color: () => syncStatusColors(downloadState),
                  width: '4rem',
                  height: '4rem',
                }}
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          {!isFullySynced && (
            <Button
              onClick={() => setShowDirtyDraftWarning(false)}
              variant="outlined"
            >
              Stay on ePCR
            </Button>
          )}

          {isFullySynced && (
            <Button
              onClick={() => {
                setShowDirtyDraftWarning(false)
                handleSubmit()
                // setSiteMode('normal')
                // setEpcrPracticeMode(false)
                // setSelectedEPCRModule('')
                // navigate('/')
              }}
              variant="contained"
              color="success"
            >
              Submit
            </Button>
          )}

          {hasSyncError && (
            <Button
              color="warning"
              variant="contained"
              onClick={() => {
                // allow leaving but keep local draft
              }}
            >
              Leave & Keep Draft
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default TempReview
